import React, { useEffect, useRef, useState, useCallback } from "react";

const days = [
  "Saturday",
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

// Create multiple copies for infinite scroll
const COPIES = 5;
const extendedDays = Array(COPIES).fill(days).flat();

interface DaySelectorProps {
  onDayChange?: (day: string) => void;
  initialDay?: string;
}

const DaySelector: React.FC<DaySelectorProps> = ({
  onDayChange,
  initialDay,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const itemHeightRef = useRef(40); // Default item height
  const isInitializedRef = useRef(false);

  // Get day index from day name
  const getDayIndex = useCallback((dayName: string) => {
    return days.findIndex((day) => day === dayName);
  }, []);

  // Get current day index based on your days array (Saturday = 0, Sunday = 1, etc.)
  const getCurrentDayIndex = useCallback(() => {
    const jsDay = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
    // Convert to your array format: Saturday = 0, Sunday = 1, etc.
    return jsDay === 0 ? 1 : jsDay === 6 ? 0 : jsDay + 1;
  }, []);

  // Initialize with initialDay or current day and center the scroll
  useEffect(() => {
    if (isInitializedRef.current) return; // Prevent re-initialization

    let initialIndex;

    if (initialDay) {
      initialIndex = getDayIndex(initialDay);
      if (initialIndex === -1) {
        // If initialDay is not valid, fall back to current day
        initialIndex = getCurrentDayIndex();
      }
    } else {
      initialIndex = getCurrentDayIndex();
    }

    setSelectedIndex(initialIndex);

    // Call onDayChange with initial day
    if (onDayChange) {
      onDayChange(days[initialIndex]);
    }

    const container = containerRef.current;
    if (container) {
      // Store the actual item height for later use
      const firstItem = container.firstElementChild as HTMLElement;
      if (firstItem) {
        itemHeightRef.current = firstItem.clientHeight;
      }

      const itemHeight = itemHeightRef.current;
      const middleCopyStart = Math.floor(COPIES / 2) * days.length;
      const targetScrollTop = (middleCopyStart + initialIndex) * itemHeight;

      // Center the current day in view
      const containerHeight = container.clientHeight;
      container.scrollTop =
        targetScrollTop - containerHeight / 2 + itemHeight / 2;
    }

    isInitializedRef.current = true;
  }, [initialDay, onDayChange, getDayIndex, getCurrentDayIndex]);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    setIsScrolling(true);

    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    const itemHeight = itemHeightRef.current;
    const center = container.scrollTop + container.clientHeight / 2;

    // Find the centered item
    const index = Math.round(center / itemHeight);
    const adjustedIndex = Math.max(0, index - 1); // Adjust for centering
    const newIndex =
      ((adjustedIndex % days.length) + days.length) % days.length;

    // Only update if the index actually changed
    if (newIndex !== selectedIndex) {
      setSelectedIndex(newIndex);

      // Call onDayChange callback
      if (onDayChange) {
        onDayChange(days[newIndex]);
      }
    }

    // Handle infinite scroll repositioning after scroll ends
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);

      const totalItems = extendedDays.length;
      const middleCopyStart = Math.floor(COPIES / 2) * days.length;
      const currentItemIndex = Math.round(center / itemHeight) - 1;

      // If we're near the beginning or end, reposition to middle copy
      if (
        currentItemIndex < days.length ||
        currentItemIndex >= totalItems - days.length
      ) {
        const newPosition =
          (middleCopyStart + newIndex) * itemHeight -
          container.clientHeight / 2 +
          itemHeight / 2;
        container.scrollTop = newPosition;
      }
    }, 200); // Increased timeout for better UX
  }, [selectedIndex, onDayChange]);

  const handleItemClick = useCallback(
    (idx: number) => {
      console.log("Clicked index:", idx);
      const container = containerRef.current;
      if (!container) return;

      // Prevent clicking during scroll
      if (isScrolling) return;

      const dayIndex = idx % days.length;

      // Update selected index immediately
      setSelectedIndex(dayIndex);

      // Call onDayChange callback
      if (onDayChange) {
        onDayChange(days[dayIndex]);
      }

      // Calculate scroll position to center the clicked item
      const itemHeight = itemHeightRef.current;
      const containerHeight = container.clientHeight;

      // Find the closest equivalent item in the middle copy for smooth scrolling
      const middleCopyStart = Math.floor(COPIES / 2) * days.length;
      const middleCopyItemIndex = middleCopyStart + dayIndex;
      const targetScrollTop = middleCopyItemIndex * itemHeight;

      // Center the item in the viewport
      const centeredScrollTop =
        targetScrollTop - containerHeight / 2 + itemHeight / 2;

      // Use smooth scrolling
      container.scrollTo({
        top: centeredScrollTop,
        behavior: "smooth",
      });
    },
    [isScrolling, onDayChange]
  );

  return (
    <div className="day-selector">
      <div
        className="day-list"
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          scrollBehavior: "smooth", // Always smooth scroll
          WebkitOverflowScrolling: "touch", // Better touch scrolling on mobile
        }}
      >
        {extendedDays.map((day, idx) => {
          const dayIndex = idx % days.length;
          const isSelected = dayIndex === selectedIndex;
          return (
            <div
              key={idx}
              className={`day-item ${isSelected ? "active" : ""}`}
              onClick={() => handleItemClick(idx)}
              style={{
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s ease",
                opacity: isSelected ? 1 : 0.7,
              }}
            >
              {day}
            </div>
          );
        })}
      </div>
      <div className="center-highlight"></div>
    </div>
  );
};

export default DaySelector;
